import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Gamepad2 } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };



  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass border-b border-white/5' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Mobile only */}
            <Link to="/" className="flex items-center gap-3 group md:hidden">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <img 
                  src="/logo.png" 
                  alt="SimplyGods" 
                  className="h-14 w-auto object-contain transition-all duration-300 group-hover:drop-shadow-[0_0_15px_rgba(252,109,0,0.5)]"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation - All links together */}
            <nav className="hidden md:flex items-center gap-8 mx-auto">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-200 link-underline ${
                  location.pathname === '/' ? 'text-white' : 'text-[#b3b3c2] hover:text-white'
                }`}
              >
                Home
              </Link>
              <div
                className="relative"
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 link-underline ${
                    location.pathname.startsWith('/hades') || location.pathname.startsWith('/ares')
                      ? 'text-white'
                      : 'text-[#b3b3c2] hover:text-white'
                  }`}
                >
                  Servere
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-56 py-2 rounded-xl glass border border-white/10 shadow-2xl overflow-hidden"
                    >
                      <Link
                        to="/hades"
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                          isActive('/hades')
                            ? 'bg-[#fc6d00]/20 text-white'
                            : 'text-[#b3b3c2] hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Gamepad2 className="w-4 h-4 text-[#fc6d00]" />
                        SimplyGods #Hades
                      </Link>
                      <Link
                        to="/ares"
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                          isActive('/ares')
                            ? 'bg-[#fc6d00]/20 text-white'
                            : 'text-[#b3b3c2] hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Gamepad2 className="w-4 h-4 text-[#fc6d00]" />
                        SimplyGods #Ares
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <Link
                to="/despre"
                className={`text-sm font-medium transition-colors duration-200 link-underline ${
                  isActive('/despre') ? 'text-white' : 'text-[#b3b3c2] hover:text-white'
                }`}
              >
                Despre Comunitate
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 glass-strong" />
            <div className="relative h-full flex flex-col pt-24 px-6">
              <nav className="flex flex-col gap-2">
                {/* Home Link */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0 }}
                >
                  <Link
                    to="/"
                    className={`block py-3 text-lg font-medium ${
                      location.pathname === '/' ? 'text-[#fc6d00]' : 'text-white'
                    }`}
                  >
                    Home
                  </Link>
                </motion.div>

                {/* Servere Dropdown */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="space-y-2">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center justify-between w-full py-3 text-lg font-medium text-white"
                    >
                      Servere
                      <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden pl-4 space-y-2"
                        >
                          <Link
                            to="/hades"
                            className={`flex items-center gap-3 py-2 text-sm ${
                              isActive('/hades') ? 'text-[#fc6d00]' : 'text-[#b3b3c2]'
                            }`}
                          >
                            <Gamepad2 className="w-4 h-4" />
                            SimplyGods #Hades
                          </Link>
                          <Link
                            to="/ares"
                            className={`flex items-center gap-3 py-2 text-sm ${
                              isActive('/ares') ? 'text-[#fc6d00]' : 'text-[#b3b3c2]'
                            }`}
                          >
                            <Gamepad2 className="w-4 h-4" />
                            SimplyGods #Ares
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>

                {/* Despre Link */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Link
                    to="/despre"
                    className={`block py-3 text-lg font-medium ${
                      isActive('/despre') ? 'text-[#fc6d00]' : 'text-white'
                    }`}
                  >
                    Despre Comunitate
                  </Link>
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
